import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function Hero() {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

  const slides = [
    { image: 'https://okayisu.com/common/img/projeler/istanbul-finans-merkezi-2019.jpg', badgeKey: 'hero.slide1.badge', yearKey: 'hero.slide1.year', titleKey: 'hero.slide1.title', accentKey: 'hero.slide1.accent', subtitleKey: 'hero.slide1.subtitle' },
    { image: 'https://okayisu.com/common/img/projeler/vadi-istanbul-2015.jpg', badgeKey: 'hero.slide2.badge', yearKey: 'hero.slide2.year', titleKey: 'hero.slide2.title', accentKey: 'hero.slide2.accent', subtitleKey: 'hero.slide2.subtitle' },
    { image: 'https://www.okayisu.com/common/img/projeler/kayseri-kadir-has-stadyumu-2018.jpg', badgeKey: 'hero.slide3.badge', yearKey: 'hero.slide3.year', titleKey: 'hero.slide3.title', accentKey: 'hero.slide3.accent', subtitleKey: 'hero.slide3.subtitle' },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setAnimating(true);
      setTimeout(() => {
        setCurrent((prev) => (prev + 1) % slides.length);
        setAnimating(false);
      }, 500);
    }, 5500);
    return () => clearInterval(timer);
  }, []);

  const goTo = (idx: number) => {
    if (idx === current) return;
    setAnimating(true);
    setTimeout(() => {
      setCurrent(idx);
      setAnimating(false);
    }, 400);
  };

  const slide = slides[current];

  const handleScrollTo = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const stats = [
    { val: '22+', lbl: t('stats.yearsExperience') },
    { val: '1000+', lbl: t('stats.completedProjects') },
    { val: '20', lbl: t('stats.countries') },
  ];

  return (
    <section id="home" className="relative w-full h-screen min-h-[480px] sm:min-h-[560px] md:min-h-[640px] overflow-hidden">
      {/* Background Image */}
      <div className={`absolute inset-0 transition-opacity duration-700 ${animating ? 'opacity-0' : 'opacity-100'}`}>
        <img src={slide.image} alt="OKAYISU LED Project" className="w-full h-full object-cover object-top" />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/65" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />
      <div className="relative z-10 h-full flex flex-col items-center justify-center px-4 sm:px-6 text-center w-full">
        <div className={`w-full transition-all duration-700 ${animating ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
          <div className="inline-flex items-center gap-2.5 bg-white/10 backdrop-blur-md border border-amber-400/40 rounded-full pl-3.5 pr-2 py-2 mb-6 sm:mb-8">
            <span className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-amber-400/20 flex items-center justify-center">
              <i className="ri-map-pin-line text-amber-400 text-sm sm:text-base" />
            </span>
            <span className="text-amber-400 text-sm sm:text-base font-bold tracking-wide whitespace-nowrap" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {t(slide.badgeKey)}
            </span>
            <span className="text-white/30 text-xs sm:text-sm font-medium">|</span>
            <span className="bg-amber-400 text-amber-950 text-xs sm:text-sm font-bold rounded-full px-3 py-1 whitespace-nowrap" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {t(slide.yearKey)}
            </span>
          </div>
          <h1 className="text-white text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-4 break-words" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            {t(slide.titleKey)}<br />
            <span className="text-amber-400">{t(slide.accentKey)}</span>
          </h1>
          <p className="text-white/80 text-sm sm:text-base md:text-lg max-w-xl mx-auto mt-3 sm:mt-4 mb-6 sm:mb-10 leading-relaxed px-2 sm:px-0" style={{ fontFamily: "'Inter', sans-serif" }}>
            {t(slide.subtitleKey)}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 md:gap-8 mb-6 sm:mb-10">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-amber-400 text-xl sm:text-2xl font-bold" style={{ fontFamily: "'Montserrat', sans-serif" }}>{stat.val}</div>
                <div className="text-white/60 text-[10px] sm:text-xs tracking-wide mt-0.5" style={{ fontFamily: "'Inter', sans-serif" }}>{stat.lbl}</div>
              </div>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-2 sm:px-0">
            <button onClick={() => handleScrollTo('#projects')} className="bg-amber-500 hover:bg-amber-400 text-white font-semibold px-6 sm:px-8 py-3.5 sm:py-4 rounded-full text-sm tracking-wide transition-all duration-300 cursor-pointer whitespace-nowrap" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {t('hero.viewProjects')}
            </button>
            <button onClick={() => handleScrollTo('#contact')} className="bg-white/15 backdrop-blur-sm border border-white/40 text-white hover:bg-white/25 hover:border-white/60 px-6 sm:px-8 py-3.5 sm:py-4 rounded-full text-sm tracking-wide transition-all duration-300 cursor-pointer whitespace-nowrap" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {t('hero.getQuote')}
            </button>
          </div>
        </div>
        <div className="absolute bottom-6 sm:bottom-10 flex items-center gap-2">
          {slides.map((_, idx) => (
            <button key={idx} onClick={() => goTo(idx)} className={`cursor-pointer transition-all duration-300 rounded-full ${idx === current ? 'w-8 h-2 bg-amber-400' : 'w-2 h-2 bg-white/50 hover:bg-white/80'}`} />
          ))}
        </div>
        <div className="absolute bottom-6 sm:bottom-10 right-4 sm:right-8 hidden md:flex flex-col items-center gap-2">
          <span className="text-white/50 text-xs tracking-widest uppercase" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t('general.scroll')}</span>
          <div className="w-px h-10 bg-gradient-to-b from-white/50 to-transparent" />
        </div>
      </div>
    </section>
  );
}